import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { SOLUTIONS_PAGES } from "@/lib/content";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";

type AnyPage = (typeof SOLUTIONS_PAGES)[keyof typeof SOLUTIONS_PAGES];

function hasContracts(p: AnyPage): p is Extract<AnyPage, { contracts: unknown }> {
  return (p as { contracts?: unknown }).contracts !== undefined;
}
function hasPlaybooks(p: AnyPage): p is Extract<AnyPage, { playbooks: unknown }> {
  return (p as { playbooks?: unknown }).playbooks !== undefined;
}
function hasOutcomes(p: AnyPage): p is Extract<AnyPage, { outcomes: unknown }> {
  return (p as { outcomes?: unknown }).outcomes !== undefined;
}
function hasCta(p: AnyPage): p is Extract<AnyPage, { cta: unknown }> {
  return (p as { cta?: unknown }).cta !== undefined;
}

export default function SolutionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const pageData = SOLUTIONS_PAGES[slug as keyof typeof SOLUTIONS_PAGES];

  if (!pageData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-h1 mb-4">Solution Not Found</h1>
          <p className="text-body-lg text-slate-500">
            The solution you are looking for does not exist.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title={pageData.seo.title}
        description={pageData.seo.description}
      />

      <section className="pt-24 pb-20 md:pt-32 md:pb-24 border-b border-border/50 bg-slate-50 dark:bg-navy/30 relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {pageData.eyebrow}
            </div>
            <h1 className="text-display mb-6 text-navy dark:text-white">
              {pageData.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              {pageData.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pains + What you get — two columns */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto">
            <div>
              <h2 className="text-h2 mb-10 text-navy dark:text-white">
                The problem
              </h2>
              <div className="space-y-10">
                {pageData.pains.map((pain, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                  >
                    <h3 className="text-h4 mb-3 text-navy dark:text-white">
                      {pain.title}
                    </h3>
                    <p className="text-body text-slate-600 dark:text-slate-400">
                      {pain.body}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-h2 mb-10 text-navy dark:text-white">
                What you get with ClauseArk
              </h2>
              <ul className="space-y-5">
                {pageData.whatYouGet.map((line, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    className="flex gap-3 items-start"
                  >
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 shrink-0 mt-1" />
                    <span className="text-body text-slate-700 dark:text-slate-300 leading-relaxed">
                      {line}
                    </span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link href="/demo">
                  <Button
                    size="lg"
                    className="bg-brand-gradient text-white border-0 h-11 px-6 no-default-hover-elevate"
                  >
                    Book a demo
                  </Button>
                </Link>
                {slug === "construction" || slug === "insurance" ? (
                  <Link href={`/pricing/${slug}`}>
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-11 px-6 no-default-hover-elevate"
                      data-testid="solutions-vertical-pricing-cta"
                    >
                      See {slug === "construction" ? "Construction" : "Insurance"}{" "}
                      pricing
                    </Button>
                  </Link>
                ) : (
                  <Link href="/pricing">
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-11 px-6 no-default-hover-elevate"
                    >
                      See pricing
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contracts (optional) */}
      {hasContracts(pageData) && (
        <section
          className="py-20 md:py-28 bg-slate-50 dark:bg-navy/30 border-t border-border/50"
          data-testid="solutions-contracts"
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mb-12">
              <h2 className="text-h2 mb-4 text-navy dark:text-white">
                {pageData.contracts.headline}
              </h2>
              <p className="text-body-lg text-slate-600 dark:text-slate-300">
                {pageData.contracts.sub}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl">
              {pageData.contracts.items.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white dark:bg-navy/60 border border-mist dark:border-white/10 rounded-2xl p-6"
                >
                  <h3 className="text-base font-semibold text-navy dark:text-white mb-2">
                    {item.name}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    {item.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Playbooks (optional) */}
      {hasPlaybooks(pageData) && (
        <section
          className="py-20 md:py-28"
          data-testid="solutions-playbooks"
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mb-12">
              <h2 className="text-h2 mb-4 text-navy dark:text-white">
                {pageData.playbooks.headline}
              </h2>
              <p className="text-body-lg text-slate-600 dark:text-slate-300">
                {pageData.playbooks.sub}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl">
              {pageData.playbooks.items.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="border border-mist dark:border-white/10 rounded-2xl p-6 flex gap-4"
                >
                  <CheckCircle2 className="h-5 w-5 text-indigo shrink-0 mt-1" />
                  <div>
                    <h3 className="text-base font-semibold text-navy dark:text-white mb-2">
                      {item.name}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Outcomes (optional) */}
      {hasOutcomes(pageData) && (
        <section
          className="py-20 md:py-28 bg-navy text-white"
          data-testid="solutions-outcomes"
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto text-center">
              {pageData.outcomes.map((o, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="px-2"
                >
                  <div className="text-2xl md:text-3xl font-bold text-cyan mb-2">
                    {o.stat}
                  </div>
                  <div className="text-sm text-slate-300 leading-relaxed">
                    {o.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA (optional) */}
      {hasCta(pageData) && (
        <section
          className="py-20 md:py-24 bg-brand-gradient text-white"
          data-testid="solutions-cta"
        >
          <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
            <h2 className="text-h2 text-white mb-4">{pageData.cta.heading}</h2>
            <p className="text-body-lg text-white/90 mb-8">
              {pageData.cta.body}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link href={pageData.cta.primary.path}>
                <Button
                  size="lg"
                  className="w-full sm:w-auto h-12 px-7 bg-white text-indigo hover:bg-slate-100 transition-colors text-base font-semibold no-default-hover-elevate"
                  data-testid="solutions-cta-primary"
                >
                  {pageData.cta.primary.label}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link href={pageData.cta.secondary.path}>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto h-12 px-7 border-white/40 bg-transparent text-white hover:bg-white/10 text-base font-semibold no-default-hover-elevate"
                  data-testid="solutions-cta-secondary"
                >
                  {pageData.cta.secondary.label}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
